import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Box,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import PropTypes from "prop-types";

import DatePickerV1 from "components/DatePickerV1";
import ButtonV1 from "components/ButtonV1";
import useToaster from "hooks/useToaster";
import { searchVendors } from "services/availabilityService";
import {
  dedupeVendorSelectOptions,
  jodlGstMatchesSellerSearchQuery,
  normalizeVendorLocationSelection,
  prependSelectAllLocationOption,
  realKeysFromLocationOptions,
  SELECT_ALL_LOCATIONS_VALUE,
  stripSelectAllToken,
  vendorLocationAutocompleteValue,
} from "utils/locationFilterUtils";
import { RESERVATION_LEDGER_FILTER_FIELDS } from "constants/availabilityConfig";

const ReservationFilters = ({
  filters,
  errors = {},
  onFilterChange,
  onFilterBlur,
  onApplyFilters,
  onResetFilters,
}) => {
  const triggerToaster = useToaster();
  const [sellerOptions, setSellerOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [allSellersData, setAllSellersData] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const debounceTimerRef = useRef(null);
  const prevVendorIdsRef = useRef([]);

  // ─── Seller search ───────────────────────────────────────────────────────────

  const fetchSellers = useCallback(async (query) => {
    if (query.length < 3) return;
    setLoadingSellers(true);
    try {
      const panList = await searchVendors(query);
      const options = dedupeVendorSelectOptions(
        panList.flatMap((pan) =>
          (pan.gst || [])
            .filter(
              (gst) =>
                gst.legal_entity === "JODL" &&
                jodlGstMatchesSellerSearchQuery(gst, query),
            )
            .map((gst) => ({
              label: gst.display_name ?? pan.pan_name ?? "-",
              value: gst.vendor_key ?? "-",
              extraInfo: `GST: ${gst.gst_number} | ${gst.legal_entity}`,
            })),
        ),
      );
      setSellerOptions(options);
      setAllSellersData(panList);
    } catch {
      triggerToaster("Failed to fetch sellers", "error");
      setSellerOptions([]);
    } finally {
      setLoadingSellers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSellerInputChange = useCallback(
    (_, newInputValue, reason) => {
      if (reason !== "input") return;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (newInputValue.length >= 2) {
        debounceTimerRef.current = setTimeout(
          () => fetchSellers(newInputValue),
          500,
        );
      }
    },
    [fetchSellers],
  );

  // ─── Location population on seller change ────────────────────────────────────

  useEffect(() => {
    prevVendorIdsRef.current = [];
    if (filters?.seller) {
      const selectedGst = allSellersData
        .flatMap((pan) => pan.gst || [])
        .find((gst) => gst.vendor_key === filters.seller);

      if (selectedGst?.locations?.length) {
        const locations = selectedGst.locations.map((loc) => {
          const place = [loc.city, loc.state].filter(Boolean).join(", ") || "-";
          const displayName =
            (loc.location_display_name &&
              String(loc.location_display_name).trim()) ||
            place;
          const vlk = loc.vendor_location_key ?? "-";
          return {
            label: displayName,
            value: vlk,
            extraInfo: `${place} | ${vlk}`,
          };
        });
        const withSelectAll = prependSelectAllLocationOption(locations);
        setLocationOptions(withSelectAll);
        const allKeys = locations.map((l) => l.value);
        prevVendorIdsRef.current = allKeys;
        onFilterChange({ name: "vendor_ids" }, allKeys);
      } else {
        setLocationOptions([]);
        onFilterChange({ name: "vendor_ids" }, []);
      }
    } else {
      setLocationOptions([]);
      onFilterChange({ name: "vendor_ids" }, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.seller]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleVendorIdsChange = (_, newValue) => {
    const rawValues = newValue.map((opt) =>
      typeof opt === "string" ? opt : opt.value,
    );
    const allRealKeys = realKeysFromLocationOptions(locationOptions);
    const next = normalizeVendorLocationSelection(
      rawValues,
      allRealKeys,
      prevVendorIdsRef.current,
    );
    prevVendorIdsRef.current = next;
    onFilterChange({ name: "vendor_ids" }, next);
  };


  const handleDateChange = (name, val, defaultTime) => {
    if (val && defaultTime) {
      const datePart = moment(val).format("YYYY-MM-DD");
      onFilterChange({ name }, `${datePart}T${defaultTime}`);
    } else {
      onFilterChange({ name }, val || null);
    }
  };

  const handleApplyFilters = () => {
    if (
      filters?.from_date &&
      filters?.to_date &&
      moment(filters.from_date).isAfter(moment(filters.to_date))
    ) {
      triggerToaster("From date cannot be after To Date", "error");
      return;
    }

    const payload = RESERVATION_LEDGER_FILTER_FIELDS.reduce(
      (collection, field) => {
        let value = filters[field.name];

        if (field.name === "vendor_ids") {
          if (Array.isArray(value))
            value = stripSelectAllToken(value.map(String));
        }

        if (
          value === undefined ||
          value === null ||
          value === "" ||
          value === "all" ||
          (Array.isArray(value) && !value.length)
        ) {
          return collection;
        }

        if (["vendor_ids", "seller"].includes(field.name)) {
          collection[field.name] = Array.isArray(value) ? value : [value];
        } else if (["reference_id", "mm_id"].includes(field.name)) {
          const trimmed = String(value).trim();
          if (trimmed) collection[field.name] = trimmed;
        } else {
          collection[field.name] = value;
        }
        return collection;
      },
      {},
    );

    onApplyFilters(payload);
  };

  const handleReset = () => {
    setAllSellersData([]);
    setSellerOptions([]);
    setLocationOptions([]);
    onResetFilters();
  };

  // ─── Location autocomplete value ─────────────────────────────────────────────

  const allRealLocationKeys = realKeysFromLocationOptions(locationOptions);
  const locationAutocompleteValue = vendorLocationAutocompleteValue(
    filters?.vendor_ids,
    allRealLocationKeys,
  );
  const locationValueObjects = locationAutocompleteValue
    .map((v) => locationOptions.find((o) => o.value === v))
    .filter(Boolean);

  const selectedSellerOption =
    sellerOptions.find((o) => o.value === filters?.seller) ?? null;

  return (
    <Box
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: grey[300],
      }}
    >
      {/* Row 1: text/select fields */}
      <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        {/* MMid */}
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <TextField
            label="MMid"
            size="small"
            fullWidth
            value={filters?.mm_id ?? ""}
            onChange={(e) => onFilterChange({ name: "mm_id" }, e.target.value)}
            onBlur={() => onFilterBlur?.({ name: "mm_id" }, filters?.mm_id)}
            error={!!errors?.mm_id}
            helperText={errors?.mm_id || ""}
          />
        </Box>

        {/* Reference ID */}
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <TextField
            label="Reference ID"
            size="small"
            fullWidth
            value={filters?.reference_id ?? ""}
            onChange={(e) =>
              onFilterChange({ name: "reference_id" }, e.target.value)
            }
          />
        </Box>

        {/* Seller */}
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Autocomplete
            size="small"
            options={sellerOptions}
            value={selectedSellerOption}
            getOptionLabel={(opt) => opt?.label ?? ""}
            onInputChange={handleSellerInputChange}
            onChange={(_, newValue) =>
              onFilterChange({ name: "seller" }, newValue?.value ?? null)
            }
            loading={loadingSellers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="All Sellers"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSellers && (
                        <CircularProgress color="inherit" size={18} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            filterOptions={(x) => x}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            noOptionsText={
              loadingSellers ? "Searching..." : "Type 3+ chars to search"
            }
          />
        </Box>

        {/* Locations */}
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Autocomplete
            multiple
            size="small"
            options={locationOptions}
            value={locationValueObjects}
            getOptionLabel={(opt) => opt?.label ?? ""}
            onChange={handleVendorIdsChange}
            disabled={!filters?.seller}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => {
              const isSelectAll = option.value === SELECT_ALL_LOCATIONS_VALUE;
              const allSelected = allRealLocationKeys.every((k) =>
                (filters?.vendor_ids || []).includes(k),
              );
              return (
                <li {...props}>
                  <input
                    type="checkbox"
                    checked={isSelectAll ? allSelected : selected}
                    readOnly
                    style={{ marginRight: 8 }}
                  />
                  {option.label}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="All Locations" />
            )}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
          />
        </Box>

        {/* Action */}
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Action</InputLabel>
            <Select
              value={filters?.action || "all"}
              label="Action"
              onChange={(e) =>
                onFilterChange({ name: "action" }, e.target.value)
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="reserve">Reserve</MenuItem>
              <MenuItem value="unreserve">Unreserve</MenuItem>
            </Select>
          </FormControl>
        </Box>


      </Box>

      {/* Row 2: dates + action buttons */}
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <DatePickerV1
          label="From Date"
          value={filters?.from_date ? new Date(filters.from_date) : null}
          onChange={(val) => handleDateChange("from_date", val, "23:59:59")}
          dateStyle={{ width: 180 }}
        />
        <DatePickerV1
          label="To Date"
          value={filters?.to_date ? new Date(filters.to_date) : null}
          onChange={(val) => handleDateChange("to_date", val, "23:59:59")}
          dateStyle={{ width: 180 }}
        />

        {/* Status */}
        <Box sx={{ ml: 2, minWidth: 150 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters?.status || "all"}
              label="Status"
              onChange={(e) =>
                onFilterChange({ name: "status" }, e.target.value)
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="CLOSED">CLOSED</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" gap={1} sx={{ ml: 4 }}>
          <ButtonV1
            title="Apply Filters"
            onClick={handleApplyFilters}
            size="medium"
          />
          <ButtonV1
            title="Reset"
            onClick={handleReset}
            size="medium"
            variant="outlined"
          />
        </Box>
      </Box>
    </Box>
  );
};

ReservationFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onFilterBlur: PropTypes.func,
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
};

export default ReservationFilters;
